import { ActivityStatusEnum, OfflineActivityStatusEnum } from "@/enums/activity";
import { PreloadedContentTypeEnum } from "@/enums/content";
import { LiveClassCategoryEnum } from "@/enums/liveClass";
import {
  FilterableStoreIdsEnum,
  ManageBatchesSortingOptionsEnum,
  ManagePastYearPaperSortingOptionsEnum,
  ManagePredesignedActivitiesSortingOptionsEnum,
  ManageQuestionPdfSortingOptionsEnum,
  PaginatedStoreIdsEnum,
} from "@/enums/stores";
import {
  DemoSessionStatusIdEnum,
  TutorSessionCategoryEnum,
  ValidTutorSessionCategoryEnum,
} from "@/enums/tutoring";
import { getIdNamePairsFromEnum, getInputOptionsFromEnum } from "@/methods/utils";

export const ManageLiveClassesCardIcons = {
  users: "Batch Names",
  alarm: "Start Date Time",
  hourglass: "Duration",
  "user-square": "Faculty",
};

export const ManageQrCodesCardIcons = {
  tags: "QR Category",
  qrcode: "QR Code",
  "info-circle": "Additional Info",
  blockquote: "Description",
};

export const StudentMarksheetReportCardIcons = {
  building: "Affiliation",
  users: "Batch",
  hourglass: "Report Status",
  "calendar-check": "Created On",
};

export const ManageOfflineActivitiesCardIcons = {
  users: "Batch",
  "calendar-check": "Created On",
  building: "Affiliation",
  "thumb-up": "Approved",
  "thumb-down": "Rejected",
  binoculars: "Pending",
};

export const PaginatedStoreSettings = Object.freeze({
  [PaginatedStoreIdsEnum.CREATE_PREDESIGNED_ACTIVITY]: {
    CategoryTabs: getIdNamePairsFromEnum(PreloadedContentTypeEnum),
    SearchFields: ["ActivityName", "Content", "Course", "Subjects", "CreatedBy", "UpdatedBy"],
    PlaceholderText: "Search by name, course, subject...",
    UiPageSize: 10,
    ApiPageSize: 500,
    EnableDownload: false,
    DownloadItems: null,
  },
  [PaginatedStoreIdsEnum.MANAGE_AFFILIATIONS]: {
    CategoryTabs: null,
    SearchFields: ["Affiliation", "EmailId", "Mobile"],
    PlaceholderText: "Search by affiliation, email, mobile",
    UiPageSize: 10,
    ApiPageSize: 500,
    EnableDownload: false,
    DownloadItems: null,
  },
  [PaginatedStoreIdsEnum.MANAGE_ACTIVITIES]: {
    CategoryTabs: getIdNamePairsFromEnum(ActivityStatusEnum),
    SearchFields: ["ActivityName", "Course", "Subjects", "Batches", "CreatedBy", "UpdatedBy"],
    PlaceholderText: "Search by activity name, subject, batch...",
    UiPageSize: 10,
    ApiPageSize: 200,
    EnableDownload: false,
    DownloadItems: null,
  },
  [PaginatedStoreIdsEnum.MANAGE_LIVE_CLASSES]: {
    CategoryTabs: getIdNamePairsFromEnum(LiveClassCategoryEnum),
    SearchFields: ["LiveClassName", "Subject", "Batch", "Faculty"],
    PlaceholderText: "Search by class name, subject, batch...",
    UiPageSize: 10,
    ApiPageSize: 200,
    EnableDownload: true,
    CardsDownloadItems: [
      "LiveClassName",
      "Faculty.Name",
      "Subject.Name",
      "StartDatetime",
      "Duration",
      "MeetingAttendeeUrl",
      "OwnerEmailId",
    ],
  },
  [PaginatedStoreIdsEnum.CONTENT_DESIGN_LIBRARY]: {
    CategoryTabs: null,
    // No client-side SearchFields — the design search is a server API; the view's own
    // searchbar drives it (see contentDesignLibraryStore.onSearchChange).
    SearchFields: null,
    PlaceholderText: "Search by tag or name",
    UiPageSize: 10,
    ApiPageSize: 30,
    EnableDownload: false,
    DownloadItems: null,
  },
  [PaginatedStoreIdsEnum.MANAGE_QR_CODES]: {
    CategoryTabs: null,
    SearchFields: [
      "QrCategory",
      "QrTitle",
      "QrDescription",
      "AdditionalInfo",
      "QrTarget",
      "Content",
    ],
    PlaceholderText: "Search by title, description, additional info...",
    IconDesciption: ManageQrCodesCardIcons,
    UiPageSize: 10,
    ApiPageSize: 1000,
    EnableDownload: false,
    DownloadItems: null,
  },
  [PaginatedStoreIdsEnum.MANAGE_USERS]: {
    CategoryTabs: null,
    SearchFields: ["UserName", "EmailId", "Mobile"],
    PlaceholderText: "Search by user name, email, mobile",
    UiPageSize: 10,
    ApiPageSize: 200,
    EnableDownload: true,
    DownloadItems: ["UserName", "EmailId", "Mobile", "Role.Name", "Batch.Name", "IsActive"],
  },
  [PaginatedStoreIdsEnum.STUDENT_MARKSHEET_REPORT]: {
    CategoryTabs: null,
    SearchFields: ["Affiliation", "Batch", "TestName"],
    PlaceholderText: "Search by test, affiliation, batch...",
    IconDesciption: StudentMarksheetReportCardIcons,
    UiPageSize: 10,
    ApiPageSize: 200,
    EnableDownload: false,
    DownloadItems: null,
  },
  [PaginatedStoreIdsEnum.TUTOR_SESSION_REPORTS]: {
    CategoryTabs: null,
    SearchFields: ["Student", "Course", "Module"],
    PlaceholderText: "Search by student, course, module",
    UiPageSize: 10,
    ApiPageSize: 200,
    EnableDownload: false,
    DownloadItems: null,
  },
  [PaginatedStoreIdsEnum.TUTORING_ADMIN_DEMO_SESSIONS]: {
    CategoryTabs: getIdNamePairsFromEnum(DemoSessionStatusIdEnum),
    SearchFields: ["SessionTitle", "Student", "Tutor", "Course"],
    PlaceholderText: "Search by title, student, tutor, course...",
    UiPageSize: 10,
    ApiPageSize: 500,
    EnableDownload: false,
    DownloadItems: null,
  },
  [PaginatedStoreIdsEnum.TUTORING_ADMIN_PAID_SESSIONS]: {
    CategoryTabs: getIdNamePairsFromEnum(TutorSessionCategoryEnum),
    SearchFields: ["SessionTitle", "Student", "Tutor", "Course"],
    PlaceholderText: "Search by title, student, tutor, course...",
    UiPageSize: 10,
    ApiPageSize: 500,
    EnableDownload: false,
    DownloadItems: null,
  },
  [PaginatedStoreIdsEnum.TUTORING_STUDENT_DASHBOARD]: {
    CategoryTabs: null,
    SearchFields: ["SessionTitle", "Course"],
    PlaceholderText: "Search by title, course...",
    UiPageSize: 10,
    ApiPageSize: 500,
    EnableDownload: false,
    DownloadItems: null,
  },
  [PaginatedStoreIdsEnum.TUTORING_TUTOR_DASHBOARD]: {
    CategoryTabs: getIdNamePairsFromEnum(ValidTutorSessionCategoryEnum),
    SearchFields: ["SessionTitle", "Course", "Student"],
    PlaceholderText: "Search by title, course, student...",
    UiPageSize: 10,
    ApiPageSize: 500,
    EnableDownload: false,
    DownloadItems: null,
  },
  // Demo-only — backs the PaginatedCards section in WidgetsShowcase (UI Kit).
  // Small UiPageSize so the pager renders with a handful of sample cards.
  [PaginatedStoreIdsEnum.WIDGETS_SHOWCASE]: {
    CategoryTabs: null,
    SearchFields: ["Title", "Subject"],
    PlaceholderText: "Search by title, subject...",
    UiPageSize: 4,
    ApiPageSize: 100,
    EnableDownload: false,
    DownloadItems: null,
  },
});

export const FilterableStoreSettings = Object.freeze({
  [FilterableStoreIdsEnum.CONTENT_COURSE_LIBRARY]: {
    CategoryTabs: null,
    SearchFields: ["CourseName"],
    PlaceholderText: "Search by course name",
    UiPageSize: 10,
    IncludeDateFilter: false,
    DateRangeDays: null,
    EnableDownload: false,
    DownloadItems: null,
  },
  [FilterableStoreIdsEnum.MANAGE_BATCHES]: {
    CategoryTabs: null,
    SearchFields: ["Course", "AcademicSession", "Batch", "Course", "Class"],
    PlaceholderText: "Search by course, session, batch, course...",
    SortFields: getInputOptionsFromEnum(ManageBatchesSortingOptionsEnum),
    UiPageSize: 10,
    IncludeDateFilter: false,
    DateRangeDays: null,
    EnableDownload: false,
    DownloadItems: null,
  },
  [FilterableStoreIdsEnum.MANAGE_OFFLINE_ACTIVITIES]: {
    CategoryTabs: getIdNamePairsFromEnum(OfflineActivityStatusEnum),
    SearchFields: ["Affiliation", "Batch", "Activity"],
    PlaceholderText: "Search by affiliation, batch, activity...",
    UiPageSize: 10,
    IncludeDateFilter: true,
    DateRangeDays: 90,
    IconDesciption: ManageOfflineActivitiesCardIcons,
    EnableDownload: false,
    DownloadItems: null,
  },
  [FilterableStoreIdsEnum.MANAGE_PREDESIGNED_ACTIVITIES]: {
    SearchFields: ["ActivityName", "Course", "Subjects", "Batches", "CreatedBy", "UpdatedBy"],
    PlaceholderText: "Search by name, course...",
    SortFields: getInputOptionsFromEnum(ManagePredesignedActivitiesSortingOptionsEnum),
    UiPageSize: 10,
    IncludeDateFilter: false,
    DateRangeDays: null,
    EnableDownload: false,
    DownloadItems: null,
  },
  [FilterableStoreIdsEnum.MANAGE_TUTORING_DEMO_STUDENTS]: {
    CategoryTabs: null,
    SearchFields: ["Student", "Course"],
    PlaceholderText: "Search by student, course...",
    IncludeDateFilter: true,
    DateRangeDays: 90,
    UiPageSize: 10,
    EnableDownload: false,
    DownloadItems: null,
  },
  [FilterableStoreIdsEnum.MANAGE_TUTORING_PAID_STUDENTS]: {
    CategoryTabs: null,
    SearchFields: ["Student", "Course"],
    PlaceholderText: "Search by student, course...",
    IncludeDateFilter: true,
    DateRangeDays: 90,
    UiPageSize: 10,
    EnableDownload: false,
    DownloadItems: null,
  },
  [FilterableStoreIdsEnum.MANAGE_TUTORING_TUTORS]: {
    CategoryTabs: null,
    SearchFields: ["Tutor", "EmailId"],
    PlaceholderText: "Search by tutor, email...",
    UiPageSize: 10,
    IncludeDateFilter: false,
    DateRangeDays: null,
    EnableDownload: false,
    DownloadItems: null,
  },
  [FilterableStoreIdsEnum.SEARCH_USERS_BY_TEXT]: {
    CategoryTabs: null,
    SearchFields: ["UserName", "EmailId", "Mobile"],
    PlaceholderText: "Search by name, email, mobile",
    UiPageSize: 10,
    IncludeDateFilter: false,
    DateRangeDays: null,
    EnableDownload: false,
    DownloadItems: null,
  },
  [FilterableStoreIdsEnum.TUTORING_MONTHLY_REPORT]: {
    CategoryTabs: null,
    SearchFields: ["ParentName", "Student", "Course"],
    PlaceholderText: "Search by student, parent, course",
    UiPageSize: 10,
    IncludeDateFilter: false,
    DateRangeDays: null,
    EnableDownload: true,
    DownloadItems: [
      "Tutor.Name",
      "Student.Name",
      "ParentName",
      "Parent.Email",
      "Parent.Mobile",
      "Course.Name",
      "StartDatetime",
      "PricePerClass",
    ],
  },
  [FilterableStoreIdsEnum.TUTORING_STUDENT_STUDY_NOTES]: {
    CategoryTabs: null,
    SearchFields: ["ModuleName", "ModuleDescription"],
    PlaceholderText: "Search by module name, description",
    UiPageSize: 10,
    IncludeDateFilter: false,
    DateRangeDays: null,
    EnableDownload: false,
    DownloadItems: null,
  },
  [FilterableStoreIdsEnum.TUTORING_TUTOR_LESSON_PLANS]: {
    CategoryTabs: null,
    SearchFields: ["ModuleName", "ModuleDescription"],
    PlaceholderText: "Search by module name, description",
    UiPageSize: 10,
    IncludeDateFilter: false,
    DateRangeDays: null,
    EnableDownload: false,
    DownloadItems: null,
  },
  [FilterableStoreIdsEnum.TUTOR_UNASSIGNED_DEMOS]: {
    CategoryTabs: null,
    SearchFields: ["Student", "Course", "SessionTitle"],
    PlaceholderText: "Search by student, course, title",
    UiPageSize: 10,
    IncludeDateFilter: false,
    DateRangeDays: null,
    EnableDownload: false,
    DownloadItems: null,
  },
  [FilterableStoreIdsEnum.MANAGE_PAST_YEAR_PAPERS]: {
    CategoryTabs: null,
    SearchFields: ["QuestionPaperName", "AcademicSession"],
    PlaceholderText: "Search by name, academic year",
    SortFields: getInputOptionsFromEnum(ManagePastYearPaperSortingOptionsEnum),
    UiPageSize: 10,
    IncludeDateFilter: false,
    DateRangeDays: null,
    EnableDownload: false,
    DownloadItems: null,
  },
  [FilterableStoreIdsEnum.MANAGE_QUESTION_PDFS]: {
    CategoryTabs: null,
    SearchFields: ["PdfUUID", "PdfFileName", "CourseName", "CreatedByName", "CreatedOn"],
    PlaceholderText: "Search by PDF, course, created by...",
    SortFields: getInputOptionsFromEnum(ManageQuestionPdfSortingOptionsEnum),
    UiPageSize: 10,
    IncludeDateFilter: false,
    DateRangeDays: null,
    EnableDownload: false,
    DownloadItems: null,
  },
  // Demo-only — backs the FilterableCards section in WidgetsShowcase (UI Kit).
  // SortFields Ids match the sample card data keys; small UiPageSize so the pager shows.
  [FilterableStoreIdsEnum.WIDGETS_SHOWCASE]: {
    CategoryTabs: null,
    SearchFields: ["Title", "Subject"],
    PlaceholderText: "Search by title, subject...",
    SortFields: [
      { Id: "Title", Name: "Title" },
      { Id: "Subject", Name: "Subject" },
    ],
    UiPageSize: 4,
    IncludeDateFilter: false,
    DateRangeDays: null,
    IconDesciption: null,
    EnableDownload: false,
    DownloadItems: null,
  },
  // Demo-only — backs the FilterableCardsPanel section in WidgetsShowcase (UI Kit).
  // Search + sort enabled to demo the (settings-gated) control row; UiPageSize is
  // unused by the panel (it renders the full filtered list, no pagination).
  [FilterableStoreIdsEnum.WIDGETS_SHOWCASE_PANEL]: {
    CategoryTabs: null,
    SearchFields: ["Title", "Subject"],
    PlaceholderText: "Search by title, subject...",
    SortFields: [
      { Id: "Title", Name: "Title" },
      { Id: "Subject", Name: "Subject" },
    ],
    UiPageSize: 100,
    IncludeDateFilter: false,
    DateRangeDays: null,
    IconDesciption: null,
    EnableDownload: false,
    DownloadItems: null,
  },
});
